import React from 'react';
import { Button } from './ui/button';
import { Star, Users, Target, Award, Sparkles, ArrowRight } from 'lucide-react';

export const About = () => {
  const scrollToSection = (sectionId) => {
    // Navigate to home page with section hash
    window.location.href = `/#${sectionId}`;
  };

  const teamMembers = [
    {
      name: "Shailee Sanghvi",
      role: "Director",
      description: "Shailee built Centre Stage Sports & Celebrities from the ground up, turning industry relationships into business success. She's the one who gets the call when brands need their biggest campaigns to work flawlessly.",
      expertise: "Known for seeing opportunities others miss and turning complex deals into win-win partnerships. Her network spans from cricket stadiums to film sets.",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      name: "Jaymin Mehta",
      role: "Director",
      description: "Jaymin turns handshakes into partnerships and ideas into revenue streams. He's the numbers guy who makes sure every deal makes sense – for everyone involved.",
      expertise: "The man behind our biggest sponsorship wins and merchandising successes. Jaymin doesn't just close deals; he builds relationships that generate value year after year.",
      gradient: "from-blue-500 to-cyan-500"
    }
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-amber-400/10 to-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-purple-400/5 to-pink-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-6 mb-20">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 font-heading">
            About
            <span className="block bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              Centre Stage Sports & Celebrities
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Born in India's entertainment heartland. We know the stars, understand what makes audiences tick, and turn big ideas into even bigger wins.
          </p>
        </div>

        {/* Vision Section */}
        <div className="mb-20">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white text-center">
            <div className="flex justify-center mb-6">
              <Target className="w-16 h-16 text-white icon-outline" />
            </div>
            <h3 className="text-4xl font-bold mb-6 font-heading">Our Vision</h3>
            <p className="text-xl leading-relaxed max-w-4xl mx-auto">
              To be the most passionately referred Celebrity & Sports Management Agency in India revered for its people, partnerships and performance
            </p>
          </div>
        </div>

        {/* Who We Are Section */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl">
                  <Sparkles className="w-8 h-8 text-white icon-outline" />
                </div>
                <h3 className="text-4xl font-bold text-gray-900 font-heading">Who We Are</h3>
              </div>

              <p className="text-xl text-gray-600 leading-relaxed">
                We're the people who make the magic happen behind the scenes. When sporting legends need someone who gets it, or when Bollywood's biggest names want partnerships that actually work, they call us. No fluff, no empty promises – just results that make everyone look good.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {['Authentic', 'Bold', 'Fierce', 'Passionate', 'Dependable', 'Respected'].map((trait, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 text-center">
                    <span className="text-blue-600 font-semibold">{trait}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl h-96">
                <img 
                  src="/images/team-collaboration.png" 
                  alt="Centre Stage Team Collaboration" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-3xl pointer-events-none"></div>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-20">
          <div className="text-center space-y-6 mb-16">
            <div className="flex justify-center">
              <Award className="w-16 h-16 text-emerald-500 icon-outline" />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 font-heading">Our Leadership Team</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the visionary leaders driving Centre Stage Sports & Celebrities to new heights of excellence and innovation.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden">
                      <img 
                        src={member.name === "Shailee Sanghvi" ? "/images/shailee.png" : "/images/jaymin.png"}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900">{member.name}</h4>
                    <p className={`text-lg font-semibold bg-gradient-to-r ${member.gradient} bg-clip-text text-transparent`}>
                      {member.role}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <p className="text-gray-600 leading-relaxed">
                      {member.description}
                    </p>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-gray-700 italic">
                        {member.expertise}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-slate-800 via-gray-800 to-gray-900 rounded-3xl p-12 text-white">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold mb-4">Our Journey in Numbers</h3>
              <p className="text-xl text-gray-300">Years of getting it right when it matters most</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-2">
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">30+</div>
                <p className="text-gray-300">Years of Experience</p>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold bg-gradient-to-r from-emerald-400 to-teal-500 bg-clip-text text-transparent">10+ Lac</div>
                <p className="text-gray-300">Tickets Sold</p>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold bg-gradient-to-r from-pink-400 to-purple-500 bg-clip-text text-transparent">300+</div>
                <p className="text-gray-300">Celebrity Associations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center pt-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to Work With Us?</h3>
            <p className="text-xl mb-8 opacity-90">
              Let's create something extraordinary together. Get in touch with our team today.
            </p>
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-white text-indigo-600 hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 px-8 py-4 text-lg"
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};